"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Select = void 0;
const lit_1 = require("lit");
const decorators_js_1 = require("lit/decorators.js");
const if_defined_js_1 = require("lit/directives/if-defined.js");
const repeat_js_1 = require("lit/directives/repeat.js");
const style_map_js_1 = require("lit/directives/style-map.js");
require("@/components/common");
require("@/components/indicators/expand-indicator/expand-indicator");
require("@/components/overlay/popover/popover");
require("@/components/overlay/select/select-listbox");
require("@/components/overlay/select/select-option");
require("@/components/overlay/sheet");
const adaptive_overlay_controller_1 = require("@/controllers/adaptive-overlay-controller");
const utils_1 = require("@/utils");
// 옵션 5개까지 보이고 나머지는 스크롤한다. 옵션 행은 small list-item 높이이고, 패널의 padding·border를 더한다.
// popover는 portal로 옮겨져 이 컴포넌트의 shadow 스타일이 닿지 않으므로 인라인으로 싣는다.
const POPOVER_MAX_HEIGHT = 'min(400px, calc(5 * var(--size-32) + 2 * (var(--overlay-panel-padding-block) + var(--border-width))))';
/**
 * popover를 프리미티브로 하는 선택 입력.
 * 좁은 화면에서는 목록을 트리거에 앵커하지 않고 sheet로 올린다. 두 표면은 backdrop·스크롤 잠금·
 * 포커스 가두기를 쥐는 방식이 달라 CSS로 갈아입힐 수 없으므로 표면 컴포넌트 자체를 갈아 끼운다.
 * 그래서 열림 상태는 두 표면이 나눠 갖지 않고 AdaptiveOverlayController가 소유한다.
 */
let Select = class Select extends lit_1.LitElement {
    constructor() {
        super(...arguments);
        this.options = [];
        this.value = '';
        /** 트리거 버튼의 variant. 배경 없이 본문에 얹을 때 ghost를 쓴다. */
        this.variant = 'tertiary';
        this.placement = 'bottom-start';
        this.ariaLabel = '';
        this.overlay = new adaptive_overlay_controller_1.AdaptiveOverlayController(this);
    }
    render() {
        return (0, lit_1.html) `
      ${this.renderTrigger()} ${this.renderPopover()} ${this.renderSheet()}
    `;
    }
    /**
     * popover·sheet는 연결되는 순간 portal 컨테이너로 자리를 옮겨 Lit의 템플릿 범위를 벗어나므로,
     * 조건부로 걷어내면 그 자리에 남는다. 표면은 늘 마운트해 두고 목록만 넣고 뺀다.
     */
    renderPopover() {
        return (0, lit_1.html) `
      <mm-popover
        placement=${this.placement}
        .anchor=${this.overlay.trigger}
        ?open=${!this.overlay.compact && this.overlay.open}
        style=${(0, style_map_js_1.styleMap)({ '--overlay-panel-max-height': POPOVER_MAX_HEIGHT })}
        @toggle=${this.overlay.handleOverlayToggle}
      >
        ${this.overlay.compact ? lit_1.nothing : this.renderOptionList()}
      </mm-popover>
    `;
    }
    renderSheet() {
        return (0, lit_1.html) `
      <mm-sheet
        aria-label=${this.ariaLabel || lit_1.nothing}
        ?open=${this.overlay.compact && this.overlay.open}
        @toggle=${this.overlay.handleOverlayToggle}
      >
        <mm-sheet-header heading=${this.ariaLabel}></mm-sheet-header>
        <mm-sheet-body>${this.overlay.compact ? this.renderOptionList() : lit_1.nothing}</mm-sheet-body>
      </mm-sheet>
    `;
    }
    /**
     * 열림 상태를 AdaptiveOverlayController가 소유하고 열 표면도 화면 폭에 따라 갈리므로, 트리거는 aria-controls로
     * 표면을 가리키지 않고 select가 클릭과 aria-expanded를 직접 배선한다. aria-controls를 걸면 표면이 같은 클릭을 또 토글한다.
     * 열린 표면의 종류만 aria-haspopup으로 알린다.
     */
    renderTrigger() {
        return (0, lit_1.html) `
      <mm-button
        variant=${this.variant}
        aria-haspopup=${this.overlay.compact ? 'dialog' : 'listbox'}
        aria-expanded=${this.overlay.open ? 'true' : 'false'}
        aria-label=${this.triggerLabel || lit_1.nothing}
        @click=${this.overlay.handleTriggerClick}
      >
        ${this.currentLabel}
        <mm-expand-indicator ?expanded=${this.overlay.open}></mm-expand-indicator>
      </mm-button>
    `;
    }
    renderOptionList() {
        return (0, lit_1.html) `
      <mm-select-listbox aria-label=${this.ariaLabel || lit_1.nothing} @input=${this.handleOptionInput}>
        ${(0, repeat_js_1.repeat)(this.options, option => option.value, option => this.renderOption(option))}
      </mm-select-listbox>
    `;
    }
    // 옵션: 선택 시 닫히며 현재 선택된 옵션은 aria-selected로 강조
    renderOption(option) {
        return (0, lit_1.html) `
      <mm-select-option
        .value=${option.value}
        label=${option.label}
        icon=${(0, if_defined_js_1.ifDefined)(option.icon)}
        ?disabled=${option.disabled}
        ?selected=${option.value === this.value}
      ></mm-select-option>
    `;
    }
    get currentLabel() {
        return this.options.find(option => option.value === this.value)?.label ?? '';
    }
    // aria-label은 버튼 콘텐츠를 대체하므로, 컨트롤 이름에 현재 값을 이어 붙여 선택값이 함께 읽히게 한다.
    get triggerLabel() {
        if (!this.ariaLabel)
            return '';
        return `${this.ariaLabel}, ${this.currentLabel}`;
    }
    willUpdate() {
        this.fillEmptyValue();
    }
    // 네이티브 select처럼 value가 비어 있으면 첫 번째 활성 옵션으로 채운다.
    fillEmptyValue() {
        if (this.value)
            return;
        this.value = this.options.find(option => !option.disabled)?.value ?? '';
    }
    // 옵션 활성화 시: 값 반영 후 목록 닫기
    handleOptionInput(event) {
        this.overlay.close();
        if (event.detail.value === this.value)
            return;
        this.value = event.detail.value;
        (0, utils_1.emit)(this, 'change', { value: this.value });
    }
};
Select.styles = (0, lit_1.css) `
    /* 트리거 콘텐츠 폭을 따른다. 패널은 트리거 폭에 맞춰 늘어나므로, 호스트가 부모를 채우면 놓인 자리마다 목록 폭이 달라진다 */
    :host {
      display: inline-flex;
    }

    /* 시각보정: indicator는 아이콘보다 큰 박스라 트리거 끝 여백이 padding보다 넓어 보인다. 그 차이만큼 바깥으로 당긴다 */
    mm-expand-indicator {
      margin-inline-end: calc(-1 * var(--space-1));
    }
  `;
__decorate([
    (0, decorators_js_1.property)({ attribute: false })
], Select.prototype, "options", void 0);
__decorate([
    (0, decorators_js_1.property)({ type: String })
], Select.prototype, "value", void 0);
__decorate([
    (0, decorators_js_1.property)({ type: String })
], Select.prototype, "variant", void 0);
__decorate([
    (0, decorators_js_1.property)({ type: String })
], Select.prototype, "placement", void 0);
__decorate([
    (0, decorators_js_1.property)({ type: String, attribute: 'aria-label' })
], Select.prototype, "ariaLabel", void 0);
Select = __decorate([
    (0, decorators_js_1.customElement)('mm-select')
], Select);
exports.Select = Select;
//# sourceMappingURL=data:application/json;base64,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